import { useEffect, useState } from "react";
import get from "lodash/get";
import { LTButton } from "@lambdatestincprivate/lt-components";
import { useSelector } from "react-redux";
import axios from "axios";
import { getApiBaseUrl, getUrlByUserDomain } from "../utils/utilityFunctions";
import { isMobileDevice } from "../utils/testHelpers";
import { ModalType } from "../components/TestDetailTopHeader/constants";

export const useMuteTest = ({
  testName,
  os,
  osName,
  osVersion,
  resolution,
  browser,
  browserVersion,
  modalType,
  isTestMuted,
  testId,
  onCloseModal,
  onMuteTest,
  userInfo,
  automationApiHost
}) => {
  const [isCombinationMuted, setIsCombinationMuted] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);

  const { addToast = () => {}, extendDuration = false, toastVariants = {} } =
    window.toast || {};


  useEffect(() => {
    if (modalType === ModalType.MUTE) {
      setStatusLoading(true);
      const apiDomain = getUrlByUserDomain(
        process.env.REACT_APP_TEST_MANAGER_BASE_URL_PREFIX ||
        window?._env_?.REACT_APP_TEST_MANAGER_BASE_URL_PREFIX
      );

      const username = get(userInfo, ["data", "username"], "");
      const accessKey = get(userInfo, ["data", "apiToken"], "");

      axios.post(
        `${apiDomain}/api/v1/tests/check-mute`,
        {
          name: testName,
          config_type: isMobileDevice({category: os}) ? "mobile" : "desktop",
          config: {
            os: osName,
            os_version: osVersion,
            resolution: resolution,
            browser: browser,
            browser_version: browserVersion,
          }
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${btoa(`${username}:${accessKey}`)}`
          }
        }
      )
      .then(response => {
        setStatusLoading(false);
        setIsCombinationMuted(response?.data?.status === "muted");
      })
      .catch(error => {
        setStatusLoading(false);
      });
    }
  }, [modalType]);

  const getMuteModalMessage = () => {
    if (!isTestMuted) {
      // Test is not muted
      if (!isCombinationMuted) {
        // Both options available
        return "Would you like to mute only this specific test case, or all its duplicates across your workspace?";
      } else {
        // Only test mute option available (combination already muted)
        return "The combination is already muted. Would you like to mute this specific test case?";
      }
    } else {
      // Test is muted
      if (isCombinationMuted) {
        // Only combination unmute option available
        return "This test is muted because the combination is muted. Would you like to unmute the combination?";
      } else {
        // Only test unmute option available (combination not muted)
        return "Would you like to unmute this specific test case?";
      }
    }
  };

  const handleMuteTest = async (isCombinationMute = false) => {
    const username = get(userInfo, ["data", "username"], "");
    const accessKey = get(userInfo, ["data", "apiToken"], "");

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Basic ${btoa(`${username}:${accessKey}`)}`
    }

    const tmsApiDomain = getUrlByUserDomain(
        process.env.REACT_APP_TEST_MANAGER_BASE_URL_PREFIX ||
        window?._env_?.REACT_APP_TEST_MANAGER_BASE_URL_PREFIX
      );

    const apiDomain = automationApiHost || getApiBaseUrl();

      const combinationMuteEndpoint = `${tmsApiDomain}/api/v1/tests/${!isCombinationMuted ? "mute" : "unmute"}`;
      const testMuteEndpoint = `${apiDomain}/api/v1/test/${testId}/mute?mute=${!isTestMuted}`;

      if (isCombinationMute) {
        const config = {
          name: testName,
          config_type: isMobileDevice({category: os}) ? "mobile" : "desktop",
          config: {
            os: osName,
            os_version: osVersion,
            resolution: resolution,
            browser: browser,
            browser_version: browserVersion,
          }
        }
        try {
          if (isCombinationMuted) {
            await axios.delete(combinationMuteEndpoint, { data: config, headers });
          } else {
            await axios.post(combinationMuteEndpoint, config, { headers });
          }
          await axios.put(testMuteEndpoint, {}, { headers });
          onMuteTest(testId, !isTestMuted);
          addToast({
            message: `Test combination has been ${!isCombinationMuted ? "muted" : "unmuted"} successfully`,
            variant: toastVariants.SUCCESS,
            extendDuration,
          });
        } catch (error) {
          addToast({
            message: `Failed to ${!isCombinationMuted ? "mute" : "unmute"} the test combination`,
            variant: toastVariants.DANGER,
            extendDuration,
          });
        }
      } else {
        try {
          await axios.put(testMuteEndpoint, {}, { headers });
          onMuteTest(testId, !isTestMuted);
          addToast({
            message: `Test has been ${!isTestMuted ? "muted" : "unmuted"} successfully`,
            variant: toastVariants.SUCCESS,
            extendDuration,
          });
        } catch (error) {
          addToast({  
            message: `Failed to ${!isTestMuted ? "mute" : "unmute"} the test`,
            variant: toastVariants.DANGER,
            extendDuration,
          });
        }
    }
  };

  const getMuteFooterButtons = () => {
    const muteTestButton = {
      isCloseButton: true,
      text: isTestMuted ? 'Unmute the test' : 'Mute this test',
      variant: LTButton.Variant.PRIMARY,
      type: "button",
      onClick: () => {
        handleMuteTest();
        onCloseModal();
      }
    };

    const muteCombinationButton = {
      isCloseButton: true,
      text: isCombinationMuted ? 'Unmute the combination' : 'Mute the combination',
      variant: LTButton.Variant.SECONDARY,
      type: "button",
      onClick: () => {
        handleMuteTest(true);
        onCloseModal();
      }
    };

    const footerButtons = [];

    // Test is not muted - user can mute test and/or combination
    if (!isTestMuted) {
      if (!isCombinationMuted) {
        // Both are unmuted: show both mute options
        footerButtons.push(muteCombinationButton, muteTestButton);
      } else {
        // Combination already muted: only show mute test option
        footerButtons.push(muteTestButton);
      }
    } 
    // Test is muted - user can unmute test and/or combination
    else {
      if (isCombinationMuted) {
        // Test muted because combination is muted: show unmute combination option
        footerButtons.push(muteCombinationButton);
      } else {
        // Test is muted independently: show unmute test option
        footerButtons.push(muteTestButton);
      }
    }

    return footerButtons;
  };

  return {
    isTestMuted,
    isCombinationMuted,
    statusLoading,
    getMuteModalMessage,
    getMuteFooterButtons
  };
};